/**
 * KB-META
 * Purpose: Role policy
 * Role: ui.module
 * EntryPoints: 
 * Inputs: 
 * Outputs: 
 * Validation: 
 * Dependencies: 
 * Contracts: 
 * Auth&Privacy: auth:none; hipaa:none; pii:none
 * Observability: analytics.feedback; analytics.wallFeedback; analytics.wall
 * Router: shard=partner-portal-cura; H2=component/role.policy; tags=ui
 * Owner: ; LastReviewed: 2025-08-25
 */
import { RoleLevel } from './roles';
import { MenuKey } from './menu.registry';

const A = RoleLevel;
const ALL_ROLES = [A.SUPER_ADMIN, A.CLINICIAN, A.CONTENT_MANAGER, A.PARTNER_ADMIN, A.SECURITY_ADMIN, A.ANALYTICS_ADMIN, A.SUPPORT_AGENT, A.AUDITOR_READONLY];

export const POLICY: Partial<Record<MenuKey, RoleLevel[]>> = {
  // 1) Clinician
  'connect.careManagerDashboard': [A.CLINICIAN, A.CONTENT_MANAGER, A.PARTNER_ADMIN, A.SECURITY_ADMIN, A.ANALYTICS_ADMIN, A.SUPPORT_AGENT, A.AUDITOR_READONLY],
  'connect.campaigns': [A.PARTNER_ADMIN],
  'connect.coach': [A.PARTNER_ADMIN],

  // Manage Admin Data (Ops)
  'ops.procedures': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.locations': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.departments': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.careTeams': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.referringSystems': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.referringSystemTeams': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.physicianArtifacts': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.requestedResources': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.customDates': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.customNoteCategories': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.medications': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.hospitalServices': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.doctorReviewReport': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.referringPhysicianReport': [A.CLINICIAN, A.PARTNER_ADMIN],
  'ops.patientDataTools': [A.CLINICIAN, A.PARTNER_ADMIN],

  // Manage Reports
  'reports.manage': [A.CLINICIAN, A.PARTNER_ADMIN],
  'reports.documents': [A.CLINICIAN, A.PARTNER_ADMIN],
  'reports.patientActivation': [A.CLINICIAN, A.PARTNER_ADMIN],
  'reports.doctorReviewPipeline': [A.CLINICIAN, A.PARTNER_ADMIN],
  'reports.refPhysicianDocs': [A.CLINICIAN, A.PARTNER_ADMIN],
  'reports.patientAttrition': [A.CLINICIAN, A.PARTNER_ADMIN],
  'reports.simpleSummary': [A.ANALYTICS_ADMIN, A.PARTNER_ADMIN, A.SUPPORT_AGENT, A.AUDITOR_READONLY],
  'reports.simpleUsageByUser': [A.ANALYTICS_ADMIN, A.PARTNER_ADMIN, A.SUPPORT_AGENT, A.AUDITOR_READONLY],
  'reports.simpleAudit': [A.ANALYTICS_ADMIN, A.PARTNER_ADMIN, A.SUPPORT_AGENT, A.AUDITOR_READONLY],
  'reports.simpleUserActivity': [A.ANALYTICS_ADMIN, A.PARTNER_ADMIN, A.SUPPORT_AGENT, A.AUDITOR_READONLY],
  'reports.workflowFunnel': [A.ANALYTICS_ADMIN, A.PARTNER_ADMIN, A.SUPPORT_AGENT, A.AUDITOR_READONLY],
  'reports.engagementHeatmap': [A.ANALYTICS_ADMIN, A.PARTNER_ADMIN, A.SUPPORT_AGENT, A.AUDITOR_READONLY],
  'reports.usageLeaderboard': [A.ANALYTICS_ADMIN, A.PARTNER_ADMIN, A.SUPPORT_AGENT, A.AUDITOR_READONLY],
  'reports.portalUsageOverview': [A.ANALYTICS_ADMIN, A.PARTNER_ADMIN, A.SUPPORT_AGENT, A.AUDITOR_READONLY],
  'reports.securityPatientAccess': [A.ANALYTICS_ADMIN, A.PARTNER_ADMIN, A.SUPPORT_AGENT, A.AUDITOR_READONLY],


  // 2) Content Manager
  'content.managePublicWall': [A.CONTENT_MANAGER, A.PARTNER_ADMIN],
  'content.approveCrowdSource': [A.CONTENT_MANAGER, A.PARTNER_ADMIN],
  'content.manageGenusTile': [A.CONTENT_MANAGER, A.PARTNER_ADMIN],
  'content.manageContactTile': [A.CONTENT_MANAGER, A.PARTNER_ADMIN],
  'content.managePathway': [A.CONTENT_MANAGER, A.PARTNER_ADMIN],
  'content.questions': [A.CONTENT_MANAGER, A.PARTNER_ADMIN],
  'content.elements': [A.CONTENT_MANAGER, A.PARTNER_ADMIN],
  'content.mingle': [A.CONTENT_MANAGER, A.PARTNER_ADMIN],
  // context while building
  // Duplicate key allowed; union handled in canSee
  // 'connect.careManagerDashboard': [...ALL_ROLES],

  // 3) Partner Admin
  'settings.managePartner': [A.PARTNER_ADMIN],
  'settings.carecode': [A.PARTNER_ADMIN],
  'settings.modules': [A.PARTNER_ADMIN],
  'settings.requestcode': [A.PARTNER_ADMIN],
  'settings.notifications': [A.PARTNER_ADMIN, A.SECURITY_ADMIN],
  'settings.careNudges': [A.PARTNER_ADMIN],
  'team.portalUsers': [A.CLINICIAN, A.PARTNER_ADMIN, A.SECURITY_ADMIN, A.SUPPORT_AGENT],
  'team.approvedMessageTeamOut': [A.PARTNER_ADMIN, A.SECURITY_ADMIN],
  'team.helpTips': [A.PARTNER_ADMIN],

  // 4) Security Admin
  // Note: team.portalUsers already includes SECURITY_ADMIN; other visibility merged above
  // 'connect.careManagerDashboard': [A.SECURITY_ADMIN],

  // 5) Analytics Admin
  'analytics.feedback': [A.ANALYTICS_ADMIN],
  'analytics.wallFeedback': [A.ANALYTICS_ADMIN],
  'analytics.wall': [A.ANALYTICS_ADMIN],
  'analytics.chat': [A.ANALYTICS_ADMIN],
  'analytics.cacheResponse': [A.ANALYTICS_ADMIN],

  
  // Dev playground (visible to super admin, partner admin, ALL via canSee super admin/all)
  'dev.analyticsDashboard': [A.PARTNER_ADMIN],
  'dev.careManagerDashboard': [A.PARTNER_ADMIN],
  'dev.focusGroups': [A.PARTNER_ADMIN],
  'dev.patientSurveyTemplate': [A.PARTNER_ADMIN],
  'dev.patientWorkflow': [A.PARTNER_ADMIN],
  'dev.modalHealth': [A.PARTNER_ADMIN],
  'dev.genusAiTestChat': [A.PARTNER_ADMIN],
  'dev.apiSchemaTester': ALL_ROLES,
  'dev.technicalDocs': ALL_ROLES,
  'dev.analytics1': [A.PARTNER_ADMIN],
  'dev.analytics2': [A.PARTNER_ADMIN],
  'dev.radar1': [A.PARTNER_ADMIN],
  'dev.radar2': [A.PARTNER_ADMIN],
  'dev.wordFlip': [A.PARTNER_ADMIN],
};

export function canSee(key: MenuKey, roles: RoleLevel[]): boolean {
  if (roles.includes(A.SUPER_ADMIN) || roles.includes(A.ALL)) return true;
  const allowed = POLICY[key];
  if (!allowed) return false;
  return roles.some(r => allowed.includes(r));
}


